/**
 * API client configuration and utilities for making authenticated requests
 */

import { config } from '@/config'

// API base URL - from centralized config (required)
const API_BASE_URL = config.apiUrl

/**
 * Error thrown when API request fails
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Normalize FastAPI's `detail` field into a human-readable string.
 *
 * Custom HTTPException handlers return a string detail (e.g. "User not found").
 * Pydantic request-validation 422s return an array of objects:
 *   [{loc: ["body", "markets"], msg: "Invalid market(s): [...]", type: "value_error"}]
 * Without normalization the array gets coerced to "[object Object],[object Object]".
 */
export function formatErrorDetail(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const parts = detail.map((entry) => {
      if (entry && typeof entry === 'object' && 'msg' in entry) {
        const e = entry as { loc?: unknown; msg: unknown }
        // Pydantic v2 prefixes field_validator messages with "Value error, " — strip it
        const msg = String(e.msg).replace(/^Value error,\s*/, '')
        const loc = Array.isArray(e.loc)
          // Drop the leading "body" / "query" segment for readability
          ? e.loc.slice(1).join('.')
          : ''
        return loc ? `${loc}: ${msg}` : msg
      }
      return typeof entry === 'string' ? entry : JSON.stringify(entry)
    })
    return parts.length === 1 ? parts[0] : parts.join('; ')
  }
  return undefined
}

/**
 * Make an authenticated API request with Bearer token
 *
 * @param endpoint - API endpoint path (e.g., '/users/me')
 * @param token - JWT access token
 * @param options - Additional fetch options
 * @returns Response data
 * @throws ApiError if request fails
 */
export async function authenticatedRequest<T>(
  endpoint: string,
  token: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    })

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      throw new ApiError('Unauthorized - session expired', 401)
    }

    // Handle 403 Forbidden - insufficient permissions
    if (response.status === 403) {
      throw new ApiError('Forbidden - insufficient permissions', 403)
    }

    // Handle 5xx server errors
    if (response.status >= 500) {
      throw new ApiError('Service unavailable', response.status)
    }

    // Handle 4xx client errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      // Extract detail message from FastAPI error response.
      // 422 request-validation responses come back as an array of objects;
      // formatErrorDetail flattens them so callers get a real string.
      const message =
        formatErrorDetail(errorData.detail) || `Request failed: ${response.statusText}`
      throw new ApiError(message, response.status, errorData)
    }

    // Handle 204 No Content (e.g., DELETE responses)
    if (response.status === 204) {
      return undefined as T
    }

    return await response.json()
  } catch (error) {
    // Network errors (e.g., API not reachable)
    if (error instanceof TypeError) {
      throw new ApiError('Network error - unable to reach API', undefined, error)
    }

    // Re-throw ApiError instances
    if (error instanceof ApiError) {
      throw error
    }

    // Unknown errors
    throw new ApiError('Unknown error occurred', undefined, error)
  }
}
